/**
 * The product shape the economics reads, and the cadence facts derived from it.
 *
 * Mirrors the canonical `PaywallProduct` contract
 * (`src/modules/builder-service/src/payments/interfaces.ts`) reduced to what the
 * math actually needs: what is charged, how often, and what the trial does.
 *
 *     type      'one_time' | 'subscription'
 *     billing   { interval: day|week|month|year, intervalCount: N }
 *     trial     { kind: 'none' | 'free' | 'paid', days, price, interval, intervalCount }
 *
 * Owner ruling R6 (2026-09-17): a PAID trial wins over a free one when both are
 * set, so `trial.kind` carries a single answer instead of two flags — a paid
 * trial has its OWN price and duration.
 *
 * Priors come from benchmark book v1 and nowhere else. The book prices the four
 * plain cadences only (every 1 day/week/month/year), so an unusual cadence
 * counts ONE payment until told otherwise — never an invented number.
 *
 * This file is pure and imports nothing.
 */

export type OfferFamily = 'monthly' | 'annual' | 'weekly' | 'daily' | 'one_time';

export type BillingInterval = 'day' | 'week' | 'month' | 'year';

export type ProductType = 'one_time' | 'subscription';

export type TrialKind = 'none' | 'free' | 'paid';

export interface ProductBilling {
  interval: BillingInterval;
  /** Periods per charge: `{ interval: 'month', intervalCount: 3 }` = quarterly. */
  intervalCount: number;
}

export interface ProductTrial {
  kind: TrialKind;
  /** Trial length in days — a FREE trial's only length; a paid trial states `interval` × `intervalCount` instead. */
  days?: number;
  /** Intro charge — PAID trials only; taken at the tap. */
  price?: number;
  /** The paid trial's own duration unit — `trialDaysOf` turns it into days (the widget, the link and the JSON never write `days` for it). */
  interval?: BillingInterval;
  intervalCount?: number;
}

/** A product as the public calculator states it (one currency for now: USD). */
export interface UnitProduct {
  id: string;
  name: string;
  price: number;
  currency: 'USD';
  type: ProductType;
  billing: ProductBilling;
  /** Absent = no trial; `trialOf` is the single reader so absence stays typed. */
  trial?: ProductTrial;
}

export const PERIODS_PER_YEAR: Record<BillingInterval, number> = {
  day: 365,
  week: 52,
  month: 12,
  year: 1,
};

export const INTERVALS: BillingInterval[] = ['day', 'week', 'month', 'year'];

/** AC-007 pins monthly 2.86 / weekly 5.36 / annual 1.00 (renewals unobservable until month 13). */
export const CHECK_DEFAULT_BY_FAMILY: Record<OfferFamily, number> = {
  monthly: 2.86,
  weekly: 5.36,
  annual: 1.0,
  daily: 1.0,
  one_time: 1.0,
};

/** The nest a product competes in — the cadence, not the price. */
export function familyOf(product: UnitProduct): OfferFamily {
  if (product.type === 'one_time') return 'one_time';
  const interval = product.billing.interval;
  if (interval === 'year') return 'annual';
  if (interval === 'month') return 'monthly';
  if (interval === 'week') return 'weekly';
  return 'daily';
}

/** Charges the cadence physically allows inside twelve months. */
export function maxChargesPerYear(product: UnitProduct): number {
  if (product.type === 'one_time') return 1;
  return Math.max(
    1,
    PERIODS_PER_YEAR[product.billing.interval] / Math.max(product.billing.intervalCount, 1),
  );
}

/**
 * A plan billed EXACTLY once a year — a subscription with `interval === 'year'
 * && intervalCount === 1` (§ D9, 2026-09-22 provider-scheme plan; final fix
 * wave FW-1/M-1). The ONE test every «yearly plan» rule reads: the counted cap
 * (`maxChargesCounted`), the widget's annual note, its D9 warning lines, its
 * chip rule and the readiness line's annual clause.
 *
 * Not the same question as «two charges fit in twelve months»
 * (`maxChargesPerYear === 2` also holds for every-6-months and every-26-weeks
 * plans, whose renewal is due at month 6), nor as `familyOf(product) ===
 * 'annual'` (which also holds for a plan billed every 2 years, renewing on day
 * 730). A trial does not change the answer.
 */
export const isYearlyPlan = (product: UnitProduct): boolean =>
  product.type === 'subscription' && product.billing.interval === 'year' && product.billing.intervalCount === 1;

/**
 * § D9 (2026-09-22 provider-scheme plan) — a plan billed EXACTLY once a year
 * (`isYearlyPlan`) may count its first renewal: `2` for it,
 * `maxChargesPerYear(product)` for every other cadence, unchanged `[R3-13]`.
 *
 * Reason: for a yearly plan the charge due on day 365 is the ONLY renewal it
 * has, and excluding it made its charge count a constant; for monthly and
 * weekly plans the charge due on day 365 is the 13th or 53rd and stays
 * out (≤ 1/12 of the year's charges — noise the owner accepted 2026-09-22). A
 * plan billed every 2 years renews on day 730, outside any 12-month reading,
 * so it keeps `1` — `maxChargesPerYear` already floors it there and
 * `hasBookPrior` is false for it (the collision spec issue 13 raised).
 *
 * The model never reads this cap: `evaluate` counts whatever `checks` it is
 * handed. It is the ceiling a HOST enforces — the widget's `checksCap` is
 * `Math.min(maxChargesCounted(product), CHECKS_CAP)`, and every widget
 * transition clamps a count to it. `maxChargesPerYear` keeps its OTHER
 * meaning — the after-year-rate boundary `Y` in `providerFeeOnCharges` —
 * everywhere, including for the cadence this function raises.
 */
export function maxChargesCounted(product: UnitProduct): number {
  return isYearlyPlan(product) ? 2 : maxChargesPerYear(product);
}

/** The benchmark book only prices the four plain cadences (every 1 period). */
export const hasBookPrior = (product: UnitProduct): boolean =>
  product.type === 'subscription' && product.billing.intervalCount === 1;

/** No prior is invented for an unusual cadence: it counts one payment until told otherwise. */
export function defaultChecks(product: UnitProduct): number {
  if (product.type === 'one_time') return 1;
  return hasBookPrior(product) ? CHECK_DEFAULT_BY_FAMILY[familyOf(product)] : 1;
}

/** Typed absence: a product without a trial reads as `{ kind: 'none' }`, never `undefined`. */
export const trialOf = (product: UnitProduct): ProductTrial => product.trial ?? { kind: 'none' };

/** Calendar days in one trial interval — a trial is a clock, so a month is 30 days, not 365 / 12. */
export const TRIAL_INTERVAL_DAYS: Record<BillingInterval, number> = {
  day: 1,
  week: 7,
  month: 30,
  year: 365,
};

/**
 * Trial length in days; 0 when there is no trial. A free trial states `days`;
 * a paid trial states its intro period as `interval` × `intervalCount` (every
 * writer — widget preset, link, JSON — leaves `days` out), so it is converted
 * here. A paid trial that does state `days` keeps them.
 */
export const trialDaysOf = (product: UnitProduct): number => {
  const trial = trialOf(product);
  if (trial.kind === 'none') return 0;
  if (trial.days !== undefined) return trial.days;
  if (trial.kind === 'free') return 0;
  return Math.max(trial.intervalCount ?? 1, 0) * TRIAL_INTERVAL_DAYS[trial.interval ?? 'day'];
};
